import { Action, Select, Selector, State, StateContext } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { catchError, Observable, of, tap, forkJoin } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { OrderService } from "../service/order.service";
import { ClearOrder, GetOrderById, GetOrders, ReloadOrders } from "./order.action";
import { orderMapper, orderDataMapper } from "../mapper/order-mapper";
import { CartState } from "../../cart/state/cart.state";
import { Order, OrderData } from "../model/order-model";
import { AuthState } from "../../auth/state/auth.state";

export class OrderStateModel {
  orders: Order;
  error: Error;
  isLoading: boolean;
}

@State<OrderStateModel>({
  name: "order",
  defaults: {
    orders: undefined!,
    error: undefined!,
    isLoading: true}})
@Injectable()
export class OrderState {
  @Select(AuthState.id) userId$: Observable<string | undefined>;
  @Select(CartState.cartId) cartId$: Observable<string>;

  constructor(private _orderService: OrderService) {}

  @Selector()
  static orderPage(state: OrderStateModel): {
    [page: number]: OrderData[];
  } {
    return state.orders?.data ?? [];
  }

  @Selector()
  static orderPagination(state: OrderStateModel) {
    return state.orders?.pagination;
  }

  @Selector()
  static orderById(state: OrderStateModel) {
    return (orderId: string) => {
      if (!state.orders || !state.orders.data) {
        return null;
      }
      const allOrders: OrderData[] = Object.values(
        state.orders.data ?? {},
      ).flat();
      return allOrders.find((order) => order.id === orderId) ?? null;
    };
  }

  @Selector()
  static isLoading(state: OrderStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static hasError(state: OrderStateModel): boolean {
    return !!state.error;
  }

  @Action(GetOrders)
  getOrders(ctx: StateContext<OrderStateModel>, action: GetOrders) {
    const state = ctx.getState();
    const orders = state.orders;

    const alreadyLoaded = orders && orders.data && orders.data[action.page];

    if (alreadyLoaded) {
      return of(orders);
    }

    ctx.patchState({
      isLoading: true});

    return this._orderService.getOrders(action.page, action.pageSize).pipe(
      switchMap((response) => {
        const newOrders = orderMapper(response);

        const currentData =
          orders &&
          typeof orders.data === "object" &&
          !Array.isArray(orders.data)
            ? orders.data
            : {};

        // First, patch state with mapped orders (fast UX)
        ctx.patchState({
          orders: {
            data: {
              ...currentData,
              [action.page]: newOrders.data[action.page]},
            pagination: newOrders.pagination},
          error: undefined!,
          isLoading: false});

        // Then, for any orders that the mapper marked as cancelable, confirm with backend
        const pageItems = newOrders.data[action.page] ?? [];
        const itemsToCheck = pageItems.filter((i) => i.cancelable === true);

        if (itemsToCheck.length === 0) {
          return of(response);
        }

        const checks = itemsToCheck.map((item) =>
          this._orderService
            .getOrderCancellation(item.id)
            .pipe(catchError(() => of(null))),
        );

        return forkJoin(checks).pipe(
          tap((results) => {
            const stateAfter = ctx.getState();
            const ordersAfter = stateAfter.orders;
            const currentPageItems =
              (ordersAfter &&
                ordersAfter.data &&
                ordersAfter.data[action.page]) ??
              [];

            const updatedPageItems = currentPageItems.map((it) => {
              const idx = itemsToCheck.findIndex((x) => x.id === it.id);
              if (idx === -1) return it;
              const res = results[idx];
              const backendCancelable = res?.cancelable;
              return {
                ...it,
                cancelable: backendCancelable === false ? false : it.cancelable};
            });

            ctx.patchState({
              orders: {
                data: {
                  ...ordersAfter.data,
                  [action.page]: updatedPageItems},
                pagination: ordersAfter.pagination},
              error: undefined!,
              isLoading: false});
          }),
          map(() => response),
        );
      }),
      catchError((error) => {
        ctx.patchState({
          error,
          isLoading: false});
        return of();
      }),
    );
  }

  @Action(ReloadOrders)
  reloadOrders(ctx: StateContext<OrderStateModel>, action: ReloadOrders) {
    ctx.patchState({
      orders: {
        data: {},
        pagination: undefined!},
      error: undefined!,
      isLoading: true});

    return ctx.dispatch(new GetOrders(action.page, action.pageSize));
  }

  @Action(GetOrderById)
  getOrderById(ctx: StateContext<OrderStateModel>, action: GetOrderById) {
    const state = ctx.getState();
    
    // Verifica se o pedido já está no state
    if (state.orders?.data) {
      const allOrders: OrderData[] = Object.values(state.orders.data).flat();
      const existingOrder = allOrders.find((order) => order.id === action.orderId);
      if (existingOrder) {
        // Pedido já existe, não precisa buscar da API
        return of(null);
      }
    }

    // Se não está no state, busca da API
    ctx.patchState({
      isLoading: true});

    return this._orderService.getOrder(action.orderId).pipe(
      tap((response) => {
        const mappedOrder = orderDataMapper(response);
        const currentData = state.orders?.data || {};
        
        // Adiciona o pedido na página 1 (ou cria se não existir)
        const page1Orders = currentData[1] || [];
        const orderExists = page1Orders.some(o => o.id === action.orderId);
        
        if (!orderExists) {
          ctx.patchState({
            orders: {
              data: {
                ...currentData,
                1: [...page1Orders, mappedOrder]},
              pagination: state.orders?.pagination || {
                total: 1,
                page: 1,
                pageSize: 10,
                totalPage: 1}},
            error: undefined!,
            isLoading: false});
        } else {
          ctx.patchState({
            isLoading: false});
        }
      }),
      catchError((error) => {
        ctx.patchState({
          error,
          isLoading: false});
        return of();
      }),
    );
  }

  @Action(ClearOrder)
  clearOrder(ctx: StateContext<OrderStateModel>) {
    ctx.patchState({
      orders: undefined!,
      isLoading: true});
    return of();
  }
}
